import { Alert, Box, Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, IconButton, InputAdornment, Paper, Snackbar, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PacienteService from "../../../services/PacienteService";
import IPaciente from "../../../types/Paciente";
import { createTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPerson from '@mui/icons-material/PersonAdd';
import HClinicaIcon from '@mui/icons-material/ListAlt';
import FolderIcon from '@mui/icons-material/Folder';
import NoteIcon from '@mui/icons-material/Note';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PacientesUtils from "../../../utils/Pacientes";
import Documentos from "./Documentos";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import HistoriaClinica from "./HistoriaClinica";
import Pacientes from "../../../utils/Pacientes";
import Citas from "./citas/Citas";
import IDocumento from "../../../types/Documento";
import CitasPasadas from "./citas/CitasPasadas";
import AgendaService from "../../../services/AgendaService";
import ICita from "../../../types/Cita";
import CitaActual from "./citas/CitaActual";
import IAlert from "../../../types/Alert";
import * as constantes from "../../../common/Constantes";
import Utilities from "../../../common/Utilities";
import ConfigUtils from "../../../utils/Config";
import Clases from "./Clases";
import BonosPaciente from "./BonosPaciente";
import { formatPhoneForDisplay } from "../../../hooks/usePhoneInput";
import { sugerenciaEmail } from "../../../utils/EmailSospechoso";

const Paciente = () => {
    const theme = createTheme();
    const history = useNavigate();
    let location = useLocation();

    const [loading, setLoading] = useState(true);
    const [paciente, setPaciente] = useState<IPaciente>();
    const [alert, setAlert] = useState<IAlert>({ open: location.state ? location.state.open : false, severity: location.state ? location.state.severity :  'info', msg: location.state ? location.state.msg :  '' });
    const [openDialog, setOpenDialog] = useState(false);
    const [verHClinica, setVerHClinica] = useState(true);
    const [verDocs, setVerDocs] = useState(false);
    const [verMas, setVerMas] = useState(false);
    const [openWarning, setOpenWarning] = useState(false);
    const [messageWarning, setMessageWarning] = useState('');
    const [linkWarning, setLinkWarning] = useState('');
    const [pacienteCI, setPacienteCI] = useState<IDocumento>();
    const [citaToday, setCitaToday] = useState<Array<ICita>>();
    const [consentimientoDoc] = useState(ConfigUtils.getConsentientoDoc());
    const [pais] = useState<string>(ConfigUtils.getPais() || 'ES');
    const [verNotas, setVerNotas] = useState(false);
    const [openDialogNotas, setOpenDialogNotas] = useState(false);
    const [loadingNotas, setLoadingNotas] = useState(false);
    const [notasPaciente, setNotasPaciente] = useState<string | undefined>();

    const [verConfig, setVerConfig] = useState(true);
    const [citaReminderPaciente, setCitaReminderPaciente] = useState<Boolean | undefined>();
    const [openReminderDialog, setOpenReminderDialog] = useState(false);
    const [pendingReminderValue, setPendingReminderValue] = useState<boolean | null>(null);
    const [loadingReminder, setLoadingReminder] = useState(false);
    const citaReminderConfig = ConfigUtils.getCitaReminder() ?? false;
    const waConfig = ConfigUtils.getWaConfig() ?? false;

    const reminderActive = citaReminderConfig || waConfig;
    // Dominio del email mal escrito (gamil.com…): los envíos rebotarían.
    const emailSugerido = sugerenciaEmail(paciente?.email);

    const userRole: number = ConfigUtils.getUserRole();
    const bonos = ConfigUtils.getBonos();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get(constantes.QP_ID);

        if (id) {
            const promisePaciente = PacienteService.get(id);
            const promiseDocCI = PacienteService.getDocCI(id);
            const promiseCitaActual = AgendaService.getCitaActualPaciente(id);

            Promise.all([promisePaciente, promiseDocCI, promiseCitaActual])
            .then((response: any) => {
                setPaciente(response[0].data);
                setCitaReminderPaciente(response[0].data.receiveReminders ?? true)
                setNotasPaciente(response[0].data.notas);
                if (response[2].data.length !== 0) setCitaToday(response[2].data);
                if (response[1].data === null) {
                    if (Utilities.isNullEmptyUndefined(response[0].data.fechaNacimiento)) {
                        setOpenWarning(true);
                        setMessageWarning('Es necesario añadir la fecha de nacimiento del paciente');
                        setLinkWarning(constantes.R_NUEVOPACIENTE);
                        return;
                    }
                    if (PacientesUtils.calcularEdad(response[0].data?.fechaNacimiento) as number > 17) {
                        if (Utilities.isNullEmptyUndefined(response[0].data.DNI)) {
                            setOpenWarning(true);
                            setMessageWarning('El paciente no tiene DNI, NIE o pasaporte guardado para poder firmar el consentimiento informado');
                            setLinkWarning(constantes.R_NUEVOPACIENTE);
                            return;
                        }
                    } else {
                        if (Utilities.isNullEmptyUndefined(response[0].data.DNItutor)) {
                            setOpenWarning(true);
                            setMessageWarning('Es necesario añadir un tutor legal para poder firmar el consentimiento informado');
                            setLinkWarning(constantes.R_TUTORLEGAL);
                            return;
                        }
                    }
                    setOpenWarning(true);
                    setMessageWarning('Es necesario firmar el consentimiento informado del paciente');
                    setLinkWarning(constantes.R_FIRMARCI);
                    
                } else {
                    setPacienteCI(response[1].data);
                }
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Paciente.Component.useEffect', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoading(false);
            });
                
        } else {
            history(constantes.R_PACIENTES);
        }
    }, [setPaciente, setLoading, setAlert, history]);

    const deletePaciente = () => {
        if (!paciente?._id) return;
        PacienteService.deleteP(paciente?._id)
            .then((response: any) => {
                history(constantes.R_PACIENTES, {state: { open: true, severity: 'success', msg: response.data.message }});
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    setOpenDialog(false);
                    const error: ILog = { method: 'Paciente.Component.deletePaciente', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            });
    };

    const updateAlert = (id: string) => {
        if (!pacienteCI?._id) {
            return;
        }
        if (id === pacienteCI?._id) {
            // La API le quita las autorizaciones al borrar el consentimiento: la ficha y los envíos lo reflejan ya
            setPaciente(prev => prev && { ...prev, authEmail: false, authWhatsApp: false });
            setOpenWarning(true);
            setMessageWarning('Es necesario firmar el consentimiento informado del paciente');
            setLinkWarning(constantes.R_FIRMARCI);
        }
    };

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const editPacienteNotas = () => {
        setLoadingNotas(true);

        PacienteService.updateNotas(paciente?._id!, { notas: notasPaciente ?? "" })
        .then(response => {
            setAlert({ open: true, severity: 'success', msg: response.data.message });
        })
        .catch (e => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'Paciente.Component.updatePacienteNotas', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setLoadingNotas(false);
            setOpenDialogNotas(false);
        });
    };

    const updateReminderConfig = async (enabled: boolean) => {
        try {
          setLoadingReminder(true);
          await PacienteService.updateConfig(paciente?._id!, { enabled });
          setCitaReminderPaciente(enabled);
          setAlert({ open: true, severity: "success", msg: "Configuración actualizada" });
        } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "Paciente.Component.updateReminderConfig",
              level: "error",
              message: e.message,
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setLoadingReminder(false);
        }
    };

    try {
        let edad;
        let esMenor: boolean = false;

        if (paciente) {
            edad = PacientesUtils.calcularEdad(paciente.fechaNacimiento);
            if (edad as number < 18) esMenor = true;
        }

        return (
            <Grid>
                <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ width: { xs: '95%', md: '55%' } }} open={openWarning && consentimientoDoc != null} onClose={() => {}}>
                    <Alert severity="warning" sx={{ width: '100%' }} onClose={() => {setOpenWarning(false)}}>
                        {messageWarning}
                        <Link to={linkWarning} state={linkWarning === constantes.R_FIRMARCI ? {paciente, esMenor} : paciente} style={{ textDecoration: 'none' }}>
                            <Button color="warning" size="small">IR</Button>
                        </Link>
                    </Alert>
                </Snackbar>
                    
                <Grid container sx={{ mb: 3}}>
                    <Grid item md={1}>
                        <Tooltip title={verNotas ? "OCULTAR NOTAS" : "VER NOTAS"} >
                            <IconButton sx={{ marginTop: theme.spacing(2), marginLeft: { xs: theme.spacing(2), sm: theme.spacing(5) } }} color="primary" style={{ backgroundColor: `${verNotas ? 'white' : '#3f51b5'}` }} onClick={() => setVerNotas(!verNotas)}>
                                <NoteIcon style={{ color: `${!verNotas ? 'white' : '#3f51b5'}`, float: 'right' }} />
                            </IconButton>
                        </Tooltip>
                        {esMenor && !paciente?.DNItutor &&
                            <Link to={constantes.R_TUTORLEGAL} state={paciente}>
                                <Tooltip title="AÑADIR TUTOR LEGAL" >
                                    <IconButton sx={{ marginTop: theme.spacing(2), marginLeft: { xs: theme.spacing(2), sm: theme.spacing(5) } }} color="primary" style={{ backgroundColor: '#3f51b5'}}>
                                        <AddPerson style={{ color: 'white', float: 'right' }} />
                                    </IconButton>
                                </Tooltip>
                            </Link>}
                        {userRole != constantes.ROLES.SECRETARIO && <Tooltip title={verHClinica ? "OCULTAR HISTORIA CLINICA" : "VER HISTORIA CLINICA"} >
                            <IconButton sx={{ marginTop: theme.spacing(2), marginLeft: { xs: theme.spacing(2), sm: theme.spacing(5) } }} color="primary" style={{ backgroundColor: `${verHClinica ? 'white' : '#3f51b5'}` }} onClick={() => setVerHClinica(!verHClinica)}>
                                <HClinicaIcon style={{ color: `${!verHClinica ? 'white' : '#3f51b5'}`, float: 'right' }} />
                            </IconButton>
                        </Tooltip>}

                        <Tooltip title={verDocs ? "OCULTAR DOCUMENTOS" : "VER DOCUMENTOS"} >
                            <IconButton sx={{ marginTop: theme.spacing(2), marginLeft: { xs: theme.spacing(2), sm: theme.spacing(5) } }} color="primary" style={{ backgroundColor: `${verDocs ? 'white' : '#3f51b5'}` }} onClick={() => setVerDocs(!verDocs)}>
                                <FolderIcon style={{ color: `${!verDocs ? 'white' : '#3f51b5'}`, float: 'right' }} />
                            </IconButton>
                        </Tooltip>

                        {reminderActive === true && <Tooltip title={verConfig ? "OCULTAR CONFIGURACIÓN" : "VER CONFIGURACIÓN"} >
                            <IconButton sx={{ marginTop: theme.spacing(2), marginLeft: { xs: theme.spacing(2), sm: theme.spacing(5) } }} color="primary" style={{ backgroundColor: `${verConfig ? 'white' : '#3f51b5'}` }} onClick={() => setVerConfig(!verConfig)}>
                                <SettingsIcon style={{ color: `${!verConfig ? 'white' : '#3f51b5'}`, float: 'right' }} />
                            </IconButton>
                        </Tooltip>}

                        {userRole == constantes.ROLES.ADMINISTRADOR && <Tooltip title="BORRAR PACIENTE" >
                            <IconButton sx={{ marginTop: theme.spacing(2), marginLeft: { xs: theme.spacing(2), sm: theme.spacing(5) } }} color="primary" style={{ backgroundColor: '#ef5350' }} onClick={() => setOpenDialog(true)}>
                                <DeleteIcon style={{ color: 'white', float: 'right' }} />
                            </IconButton>
                        </Tooltip>}
                    </Grid>

                    <Grid item md={7} xs={12}>
                        <Container>
                            <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', marginLeft: '10px' }} >DATOS PERSONALES</Typography>
                                    <Link to={constantes.R_NUEVOPACIENTE} state={paciente} style={{ textDecoration: 'none' }}>
                                        <Tooltip title="EDITAR DATOS PERSONALES" >
                                            <IconButton>
                                                <EditIcon style={{ float: 'right', cursor: 'pointer', color: 'grey' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Link>
                                </div>

                                {loading ? <CircularProgress /> :
                                    <Grid container>
                                        <>
                                        <Grid item md={2} xs={12}>
                                            <TextField label="Nº de paciente" value={paciente?.internalID != null ? paciente?.internalID.toString() : '#'} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                        </Grid>
                                        {/* Cada fila suma 11 columnas: el campo (margen + 90%) es algo más ancho que su columna y necesita holgura */}
                                        <Grid item md={5} xs={12}>
                                            <TextField label="Nombre" value={paciente?.nombre != null && paciente?.apellidos != null ? `${paciente?.nombre} ${paciente?.apellidos}` : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <TextField label={paciente?.DNI ? Utilities.getIdLabel(paciente?.DNI, pais) : (pais === 'ES' ? "DNI, NIE o pasaporte" : Utilities.getNifLabel(pais))} value={paciente?.DNI != null ? paciente?.DNI : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <TextField label="Edad" value={paciente?.fechaNacimiento != null ? `${PacientesUtils.calcularEdad(paciente.fechaNacimiento)} años` : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                        </Grid>

                                        <Grid item md={paciente?.telefonoAdicional ? 3 : 4} xs={12}>
                                            <TextField label="Teléfono" value={formatPhoneForDisplay(paciente?.telefono || '')} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                        </Grid>
                                        {paciente?.telefonoAdicional &&
                                        <Grid item md={3} xs={12}>
                                            <TextField label="Teléfono adicional" value={formatPhoneForDisplay(paciente?.telefonoAdicional || '')} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                        </Grid>
                                        }
                                        <Grid item md={paciente?.telefonoAdicional ? 5.3 : 7.5} xs={12}>
                                            <TextField
                                              label="Email"
                                              value={paciente?.email != null ? paciente?.email : ''}
                                              style={{ marginLeft: '20px', marginTop: '15px', marginBottom: '10px', width: '90%' }}
                                              // El aviso va dentro del campo para no descuadrar la fila
                                              sx={emailSugerido ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' } } : undefined}
                                              InputProps={{
                                                readOnly: true,
                                                endAdornment: emailSugerido ? (
                                                  <InputAdornment position="end">
                                                    <Tooltip title={`El email parece mal escrito: ¿${emailSugerido}?`} enterTouchDelay={0} leaveTouchDelay={4000}>
                                                      <WarningAmberIcon color="warning" fontSize="small" />
                                                    </Tooltip>
                                                  </InputAdornment>
                                                ) : undefined
                                              }}
                                            />
                                        </Grid>

                                        </>
                                        {edad as number < 18 && paciente?.DNItutor &&
                                            <>
                                                <Grid item xs={12}>

                                                    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', marginLeft: '10px' }} >TUTOR LEGAL</Typography>
                                                        <Link to={constantes.R_TUTORLEGAL} state={paciente} style={{ textDecoration: 'none' }}>
                                                            <Tooltip title="EDITAR TUTOR LEGAL">
                                                                <IconButton>
                                                                    <EditIcon style={{ float: 'right', cursor: 'pointer', color: 'grey' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Link>
                                                    </div>

                                                    {loading ? <CircularProgress /> :

                                                        <Grid container>
                                                            <Grid item md={7} lg={6} xs={12}>
                                                                <TextField label="Nombre" value={paciente?.nombreTutor != null && paciente?.apellidosTutor != null ? `${paciente?.nombreTutor} ${paciente?.apellidosTutor}` : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                                            </Grid>
                                                            <Grid item md={4} xs={12}>
                                                                <TextField label={Utilities.getIdLabel(paciente?.DNItutor, pais, true)} value={paciente?.DNItutor != null ? paciente?.DNItutor : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                                            </Grid>
                                                        </Grid>
                                                    }


                                                </Grid>
                                            </>
                                        }
                                        <Grid item md={12}>
                                            {verMas ?
                                                <Tooltip title="VER MENOS INFORMACIÓN" >
                                                    <IconButton onClick={() => setVerMas(false)}>
                                                        <RemoveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                :
                                                <Tooltip title="VER MÁS INFORMACIÓN" >
                                                    <IconButton onClick={() => setVerMas(true)}>
                                                        <AddIcon />
                                                    </IconButton>
                                                </Tooltip>}
                                        </Grid>
                                        {verMas &&
                                            <>
                                                <Grid item md={12} xs={12}>
                                                    <TextField label="Domicilio" value={paciente?.domicilio != null ? paciente?.domicilio : ''} style={{ marginLeft: '20px', marginTop: '15px' }} sx={{ width: { xs: '90%', md: '95%' } }} InputProps={{ readOnly: true }} />
                                                </Grid>
                                                <Grid item md={2} xs={12}>
                                                    <TextField label="Sexo" value={paciente?.sexo != null ? Pacientes.getSexo(paciente?.sexo) ?? '' : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                                </Grid>
                                                <Grid item md={4} xs={12}>
                                                    <TextField label="¿Cómo nos ha conocido?" value={paciente?.conocido != null ? Pacientes.getConocido(paciente?.conocido) ?? '' : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                                </Grid>
                                                <Grid item md={6} xs={12}>
                                                    <TextField label="Más info" value={paciente?.masConocido != null ? paciente?.masConocido : ''} style={{ marginLeft: '20px', marginTop: '15px', width: '90%' }} InputProps={{ readOnly: true }} />
                                                </Grid>
                                                {/* En móvil, mismo ancho que el resto de campos (90% de la fila completa) */}
                                                <Grid item md={12} xs={12}>
                                                    <TextField multiline label="Deportes, aficiciones, actividad física habital" value={paciente?.actividadFisica != null ? paciente?.actividadFisica : ''} style={{ marginLeft: '20px', marginTop: '15px' }} sx={{ width: { xs: '90%', md: '95%' } }} InputProps={{ readOnly: true }} />
                                                    <TextField multiline label="Ocupación laboral y condiciones del lugar laboral" value={paciente?.ocupacion != null ? paciente?.ocupacion : ''} style={{ marginLeft: '20px', marginTop: '15px' }} sx={{ width: { xs: '90%', md: '95%' } }} InputProps={{ readOnly: true }} />
                                                    <TextField multiline label="¿Has acudido antes a algún profesional de la salud? ¿Por qué? ¿Cuándo?" value={paciente?.especialistaSalud != null ? paciente?.especialistaSalud : ''} style={{ marginLeft: '20px', marginTop: '15px' }} sx={{ width: { xs: '90%', md: '95%' } }} InputProps={{ readOnly: true }} />
                                                </Grid>
                                            </>}
                                    </Grid>
                                }
                            </Paper>
                                {verHClinica && paciente && <>
                                    {userRole != constantes.ROLES.SECRETARIO && <HistoriaClinica paciente={paciente} />}
                                    {userRole != constantes.ROLES.SECRETARIO && citaToday && citaToday?.length > 0 && citaToday.map((cita: ICita) => {
                                        if (cita.asistencia === false) return null;
                                        return (
                                            <CitaActual paciente={paciente} cita={cita} esMenor={esMenor} key={cita._id} />
                                        );
                                    })}
                                </>}
                        </Container>
                    </Grid>

                    <Grid item md={4} xs={12}>
                    {paciente &&
                        <>
                            {/* RECORDATORIOS */}
                            {reminderActive && verConfig && <>
                              <Container maxWidth="md">
                                <Paper
                                  elevation={3}
                                  sx={{
                                    marginTop: theme.spacing(3),
                                    padding: theme.spacing(3),
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                <Typography variant="h6" fontWeight="bold">
                                  Recordatorios de citas
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                                  Envío automático de recordatorios antes de la cita
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={citaReminderPaciente as boolean}
                                      onChange={(e) => {
                                        const newValue = e.target.checked;
                                        if (!newValue) {
                                          setPendingReminderValue(newValue);
                                          setOpenReminderDialog(true);
                                        } else {
                                          updateReminderConfig(true);
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    loadingReminder
                                      ? "Guardando..."
                                      : citaReminderPaciente
                                        ? "Recibe recordatorios"
                                        : "No recibe recordatorios"
                                  }
                                />
                                {/* DETALLE DE CANALES — solo si el paciente quiere recordatorios */}
                                {citaReminderPaciente && (
                                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                                    {/* CANAL WHATSAPP — solo si la clínica lo tiene activo */}
                                    {waConfig && (
                                      <Alert
                                        severity={paciente?.authWhatsApp && paciente?.telefono ? 'success' : 'warning'}
                                        icon={<span style={{ fontSize: 16 }}>💬</span>}
                                      >
                                        {paciente?.authWhatsApp && paciente?.telefono
                                          ? 'Recibirá el recordatorio por WhatsApp.'
                                          : <>
                                              No recibirá el recordatorio por WhatsApp porque:
                                              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                                                {!paciente?.telefono && <li>No tiene teléfono registrado</li>}
                                                {!paciente?.authWhatsApp && <li>No ha autorizado WhatsApp</li>}
                                              </ul>
                                            </>
                                        }
                                      </Alert>
                                    )}

                                    {/* CANAL EMAIL — solo si la clínica lo tiene activo */}
                                    {citaReminderConfig && (
                                      <Alert
                                        severity={paciente?.email && paciente?.authEmail && !emailSugerido ? 'success' : 'warning'}
                                        icon={<span style={{ fontSize: 16 }}>✉️</span>}
                                      >
                                        {paciente?.email && paciente?.authEmail
                                          ? emailSugerido
                                            ? 'El email parece mal escrito: es probable que el recordatorio no llegue.'
                                            : waConfig && paciente?.authWhatsApp && paciente?.telefono
                                            ? 'Email disponible como alternativa si el envío por WhatsApp no es posible.'
                                            : 'Recibirá el recordatorio por email.'
                                          : <>
                                              No recibirá el recordatorio por email porque:
                                              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                                                {!paciente?.email && <li>No tiene email registrado</li>}
                                                {!paciente?.authEmail && <li>No ha autorizado el envío de emails</li>}
                                              </ul>
                                            </>
                                        }
                                      </Alert>
                                    )}

                                    {/* NINGÚN CANAL DISPONIBLE */}
                                    {((waConfig && (!paciente?.authWhatsApp || !paciente?.telefono)) || !waConfig) &&
                                     ((citaReminderConfig && (!paciente?.email || !paciente?.authEmail)) || !citaReminderConfig) && (
                                      <Alert severity="error">
                                        Este paciente no recibirá ningún recordatorio. No hay ningún canal disponible.
                                      </Alert>
                                    )}

                                  </Box>
                                )}
                                </Paper>
                              </Container>
                              <Dialog
                                open={openReminderDialog}
                                onClose={() => setOpenReminderDialog(false)}
                              >
                                <DialogTitle>Desactivar recordatorios</DialogTitle>
                                <DialogContent>
                                  <DialogContentText>
                                    Si desactivas los recordatorios, el paciente dejará de recibir notificaciones
                                    antes de sus citas. Esta acción puede afectar a la asistencia.
                                  </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                  <Button onClick={() => setOpenReminderDialog(false)}>
                                    Cancelar
                                  </Button>
                                  <Button
                                    color="error"
                                    variant="contained"
                                    onClick={() => {
                                      if (pendingReminderValue !== null) {
                                        updateReminderConfig(pendingReminderValue);
                                      }
                                    
                                      setOpenReminderDialog(false);
                                      setPendingReminderValue(null);
                                    }}
                                  >
                                    Desactivar
                                  </Button>
                                </DialogActions>
                              </Dialog>
                            </>}
                            {verNotas && 
                                <Container maxWidth="md">
                                  <Paper
                                    elevation={3}
                                    sx={{
                                      marginTop: theme.spacing(3),
                                      padding: theme.spacing(3),
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 2,
                                      }}
                                    >
                                      <Typography variant="h6" sx={{ fontWeight: 'bold', ml: 1 }}>
                                        NOTAS
                                      </Typography>
                                  
                                      <Tooltip title={notasPaciente ? "Modificar nota" : "Añadir nota"}>
                                        <IconButton sx={{ color: 'grey' }} onClick={() => setOpenDialogNotas(true)}>
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  
                                    {notasPaciente ? (
                                      <div style={{ whiteSpace: "pre-line" }}>{notasPaciente}</div>
                                    ) : (
                                      <div>No se han encontrado notas del paciente</div>
                                    )}
                                  </Paper>
                                </Container>
                            }
                            <Citas paciente={paciente} />
                            <Clases paciente={paciente!} />
                            { bonos.length > 0 && <BonosPaciente paciente={paciente!} />}
                            { verDocs && <Documentos paciente={paciente} callBack={updateAlert} />}
                        </>
                    }
                    </Grid>
                    
                    
                    <Grid container sx={{ px: { xs: '16px', sm: '24px' } }}>
                      <Grid item xs={12} md={1} />
                                    
                      <Grid item xs={12} md={11}>
                        {userRole != constantes.ROLES.SECRETARIO && verHClinica && paciente && (
                          <CitasPasadas
                            paciente={paciente}
                            esMenor={esMenor}
                          />
                        )}
                      </Grid>
                    </Grid>
                </Grid>

                {/* BORRAR PACIENTE */}
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                    <DialogTitle>BORRAR PACIENTE: {paciente?.nombre} {paciente?.apellidos}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>¿Está seguro de que desea borrar el paciente?</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => deletePaciente()} color="primary">SI</Button>
                        <Button onClick={() => setOpenDialog(false)} color="primary" autoFocus>NO</Button>
                    </DialogActions>
                </Dialog>

                {/* NOTAS DEL PACIENTE */}
                <Dialog open={openDialogNotas} onClose={() => {setOpenDialogNotas(false); setNotasPaciente(paciente?.notas!)}} maxWidth="sm" fullWidth>
                  <DialogTitle>{paciente?.notas ? 'Modificar notas del paciente' : 'Añadir notas al paciente'}</DialogTitle>
                  <DialogContent>          
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          value={notasPaciente}
                          onChange={(e) => setNotasPaciente(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          multiline
                          rows={4}
                          inputProps={{ maxLength: 500 }}
                          helperText={`${notasPaciente?.length ?? 0}/500 caracteres`}
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>          

                  <DialogActions>
                    {loadingNotas ? (
                      <Grid container>
                        <CircularProgress style={{ margin: '10px auto' }} />
                      </Grid>
                    ) : (
                      <>
                        <Button
                          onClick={editPacienteNotas}
                          color="primary"
                        >
                          GUARDAR
                        </Button>
                        <Button onClick={() => {setOpenDialogNotas(false); setNotasPaciente(paciente?.notas!)}} color="primary" autoFocus>
                          CANCELAR
                        </Button>
                      </>
                    )}
                  </DialogActions>
                </Dialog>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </Grid>
        );
    } catch (e: any) {
        const error: ILog = { method: 'Paciente.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }


};

export default Paciente;